import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import {
  FiChevronDown,
  FiCreditCard,
  FiEdit3,
  FiGrid,
  FiKey,
  FiLink,
  FiLogOut,
  FiShoppingCart,
  FiZap,
} from "react-icons/fi";
import { LOG_OUT } from "../../constant";
import {
  getAuthAvatarUrl,
  getAuthDisplayName,
  getAuthInitials,
  withCacheBust,
} from "../../utils/userIdentity";
import { isDealerUser } from "../../utils/userTypes";
import { hasDealerPortalAccess } from "../../utils/dealerSessionSync";
import AccountQuickModal from "../AccountQuickModal";
import "./UserAccountMenu.css";

const CLICK_GUARD_MS = 400;
let clickGuardTimer = null;

function armClickGuard() {
  if (typeof document === "undefined") return;
  document.body.classList.add("pi-click-guard");
  if (clickGuardTimer) {
    window.clearTimeout(clickGuardTimer);
  }
  clickGuardTimer = window.setTimeout(() => {
    document.body.classList.remove("pi-click-guard");
    clickGuardTimer = null;
  }, CLICK_GUARD_MS);
}

const MENU_ITEMS = [
  { id: "profile", label: "Edit profile", panel: "profile", Icon: FiEdit3 },
  { id: "credits", label: "Credits & evaluations", panel: "credits", Icon: FiCreditCard },
  {
    id: "password",
    label: "Change password",
    panel: "password",
    Icon: FiKey,
  },
  { id: "referral", label: "Invite friends", panel: "referral", Icon: FiLink },
];

const DEALER_LINKS = [
  {
    id: "dealers-dash",
    label: "Dealer dashboard",
    to: "/dealers/dashbord",
    Icon: FiGrid,
  },
  {
    id: "dealers-auction",
    label: "Auctions",
    to: "/dealers/dashbord/auction",
    Icon: FiZap,
  },
  {
    id: "dealers-buynow",
    label: "Buy now",
    to: "/dealers/dashbord/buynow",
    Icon: FiShoppingCart,
  },
];

function UserAccountMenu({ auth, logOut, variant = "header", onLogout }) {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState(null);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const lastPanelRef = useRef("profile");
  const rootRef = useRef(null);
  const history = useHistory();
  const name = getAuthDisplayName(auth);
  const initials = getAuthInitials(auth);
  const avatarUrl = useMemo(() => {
    const url = getAuthAvatarUrl(auth);
    return url ? withCacheBust(url) : "";
  }, [auth?.image, auth?.token, auth?.id]);
  const email = auth?.email || auth?.user?.email || auth?.username || "";
  const showAvatar = Boolean(avatarUrl) && !avatarFailed;
  const showDealerMenu =
    isDealerUser(auth) || hasDealerPortalAccess(auth);

  useEffect(() => {
    setAvatarFailed(false);
  }, [avatarUrl]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handleLogout = () => {
    setOpen(false);
    armClickGuard();
    logOut();
    if (typeof onLogout === "function") {
      onLogout();
      return;
    }
    history.push("/");
  };

  const openPanel = (nextPanel) => {
    setOpen(false);
    armClickGuard();
    lastPanelRef.current = nextPanel;
    setPanel(nextPanel);
  };

  return (
    <div
      className={`pi-account${open ? " is-open" : ""} pi-account--${variant}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="pi-account__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="pi-account__avatar" aria-hidden>
          {showAvatar ? (
            <img
              src={avatarUrl}
              alt=""
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <span className="pi-account__initials">{initials}</span>
          )}
        </span>
        <span className="pi-account__meta">
          <span className="pi-account__name">{name}</span>
          {email ? <span className="pi-account__email">{email}</span> : null}
        </span>
        <FiChevronDown size={16} className="pi-account__chevron" aria-hidden />
      </button>

      {open ? (
        <div className="pi-account__menu" role="menu">
          <div className="pi-account__menu-head">
            <span className="pi-account__avatar pi-account__avatar--lg" aria-hidden>
              {showAvatar ? (
                <img
                  src={avatarUrl}
                  alt=""
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <span className="pi-account__initials">{initials}</span>
              )}
            </span>
            <div>
              <p className="pi-account__menu-name">{name}</p>
              {email ? <p className="pi-account__menu-email">{email}</p> : null}
              {showDealerMenu ? (
                <p className="pi-account__menu-role">Dealer account</p>
              ) : null}
            </div>
          </div>

          {showDealerMenu ? (
            <div className="pi-account__section" aria-label="Dealer shortcuts">
              {DEALER_LINKS.map(({ id, label, to, Icon }) => (
                <Link
                  key={id}
                  to={to}
                  className="pi-account__item"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    armClickGuard();
                  }}
                >
                  <Icon size={16} aria-hidden />
                  <span>{label}</span>
                </Link>
              ))}
            </div>
          ) : null}

          {MENU_ITEMS.map(({ id, label, panel: nextPanel, Icon }) => (
            <button
              key={id}
              type="button"
              className="pi-account__item"
              role="menuitem"
              onClick={() => openPanel(nextPanel)}
            >
              <Icon size={16} aria-hidden />
              <span>{label}</span>
            </button>
          ))}

          <button
            type="button"
            className="pi-account__item pi-account__item--danger"
            role="menuitem"
            onClick={handleLogout}
          >
            <FiLogOut size={16} aria-hidden />
            <span>Log out</span>
          </button>
        </div>
      ) : null}

      <AccountQuickModal
        open={Boolean(panel)}
        panel={panel || lastPanelRef.current}
        onClose={() => setPanel(null)}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = (dispatch) => ({
  logOut: () => dispatch({ type: LOG_OUT }),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserAccountMenu);
